import React, {useCallback, useEffect, useRef, useState } from "react";
import "./css/SearchBar.css";
import "./css/SearchResults.css";
import axios from "axios";
import {DraggableModalBox} from "./DraggableModalBox";
import { useSaveSearchDialog } from "./SaveSearchDialog";
import { FaPrint, FaFilePdf, FaFileWord, FaFileExcel, FaEllipsisV } from "react-icons/fa";
import * as XLSX from "xlsx";
export default function ProfilerKeywordSearch({
                                                  mode = "profiler",
                                                  featureIds = [],
                                                  featureIdsByModule = {},
                                                  vendorCategoryIds = [],
                                                  productCategoryIds = [],
                                                  hasSubmittedSearch = false,
                                                  onHasSubmittedSearchChange,
                                                  openSheet,
                                                  onClearAll,
                                                  clearResultsToken = 0,
                                                  keywordValue,
                                                  onKeywordChange,
                                                  searchRequestHistory = [],
                                                  replaySearchHistoryToken = 0,
                                                  discoverKeywordTargetValue = "both",
                                                  onDiscoverKeywordTargetChange,
                                                  onSearchRequestHistoryUpsert,
                                                  onSearchRequestHistoryReplace,
                                              }) {
    const KEYWORD_TARGETS = {
        BOTH: "both",
        VENDORS_ONLY: "vendor",
        PRODUCTS_ONLY: "product",
    };

    const [keyword, setKeyword] = useState(keywordValue || "");
    const [isLoading, setIsLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedVendorId, setSelectedVendorId] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [activeTab, setActiveTab] = useState("products");
    const [ctxMenu, setCtxMenu] = useState({ visible: false, x: 0, y: 0, type: null, item: null });
    const [productCount, setProductCount] = useState(0);
    const [vendorCount,  setVendorCount]  = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const PAGE_SIZE = 50;
    const lastSearchRef = useRef(null);
    const { openSaveDialog, saveDialogPortal } = useSaveSearchDialog();
    const [selectedCompareIds, setSelectedCompareIds] = useState([]);
    const [searchModeMenuOpen, setSearchModeMenuOpen] = useState(false);
    const [discoverKeywordTarget, setDiscoverKeywordTarget] = useState(
        discoverKeywordTargetValue || KEYWORD_TARGETS.BOTH
    );

    const [showDataExportMenu, setShowDataExportMenu] = useState(false);
    const [showFuncExportMenu, setShowFuncExportMenu] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestAbortRef = useRef(null);
    const suggestTimerRef = useRef(null);

    const SEARCH_MODES = {
        DISCOVER: "discover",
        PRODUCT: "product",
        VENDOR: "vendor",
    };
    const [searchMode, setSearchMode] = useState(SEARCH_MODES.DISCOVER);
    const prevKeywordValueRef = useRef(keywordValue);
    const suppressKeywordSyncRef = useRef(false);
    const suppressAutoSearchRef = useRef(false);
    const searchRequestIdRef = useRef(0);
    const searchTriggerRef = useRef(null);
    const searchRequestHistoryRef = useRef(Array.isArray(searchRequestHistory) ? searchRequestHistory : []);
    const previousSearchInputsRef = useRef({
        featureIdsKey: "",
        vendorCategoryIdsKey: "",
        productCategoryIdsKey: "",
        discoverKeywordTarget: String(discoverKeywordTargetValue || "").trim() || KEYWORD_TARGETS.BOTH,
    });

    const updateSearchResultsState = (data) => {
        var nextVendors = data?.vendors || [];
        var nextProducts = data?.products || [];

        setVendors(nextVendors);
        setProducts(nextProducts);
        setProductCount(
            typeof data?.productCount === "number"
                ? data.productCount
                : nextProducts.length
        );
        setVendorCount(
            typeof data?.vendorCount === "number"
                ? data.vendorCount
                : nextVendors.length
        );

        if (searchMode === SEARCH_MODES.VENDOR && nextVendors.length > 0) {
            setActiveTab("vendors");
        } else if (nextProducts.length > 0) {
            setActiveTab("products");
        } else if (nextVendors.length > 0) {
            setActiveTab("vendors");
        }

        return {
            vendors: nextVendors,
            products: nextProducts,
        };
    };

    useEffect(() => {
        searchRequestHistoryRef.current = Array.isArray(searchRequestHistory) ? searchRequestHistory : [];
    }, [searchRequestHistory]);

    useEffect(() => {
        const next = String(discoverKeywordTargetValue || "").trim() || KEYWORD_TARGETS.BOTH;
        setDiscoverKeywordTarget(next);
    }, [discoverKeywordTargetValue]);

   

    // Sync with parent keyword value only when it changes (Discover mode)
    useEffect(() => {
        if (searchMode !== SEARCH_MODES.DISCOVER) {
            prevKeywordValueRef.current = keywordValue;
            return;
        }

        if (suppressKeywordSyncRef.current) {
            suppressKeywordSyncRef.current = false;
            prevKeywordValueRef.current = keywordValue;
            return;
        }

        if (keywordValue !== prevKeywordValueRef.current) {
            const next = keywordValue || "";
            setKeyword(next);
            prevKeywordValueRef.current = keywordValue;
        }
    }, [keywordValue, searchMode]);


    const hasAnythingSelected =
        keyword.trim().length > 0 ||
        featureIds.length > 0 ||
        vendorCategoryIds.length > 0 ||
        productCategoryIds.length > 0 ||
        vendors.length > 0 ||
        products.length > 0;

    const submitDiscoverKeyword = (rawKeyword) => {
        const trimmed = String(rawKeyword || "").trim();
        if (!trimmed) return false;

        searchTriggerRef.current = SEARCH_MODES.DISCOVER;
        onKeywordChange?.(trimmed);
        return true;
    };

    const submitExactKeyword = (rawValue, modeType) => {
        const trimmed = String(rawValue || "").trim();
        if (!trimmed) return false;
        searchTriggerRef.current = modeType;
        onKeywordChange?.(trimmed);
        return true;
    };

    const handleKeywordInputKeyDown = (e) => {
        const trimmed = keyword.trim();
        if (e.key !== "Enter" || trimmed.length === 0) return;

        // only search if enter was pressed
        e.preventDefault();

        if (searchMode === SEARCH_MODES.DISCOVER) {
            submitDiscoverKeyword(trimmed);
        } else if (searchMode === SEARCH_MODES.PRODUCT) {
            submitExactKeyword(trimmed, SEARCH_MODES.PRODUCT);
        } else if (searchMode === SEARCH_MODES.VENDOR) {
            submitExactKeyword(trimmed, SEARCH_MODES.VENDOR);
        }

        onHasSubmittedSearchChange?.(true);
    };

    const splitTwoWordsPerLine = (name = "") => {
        const words = String(name).trim().split(/\s+/).filter(Boolean);
        const lines = [];
        for (let i = 0; i < words.length; i += 2) {
            lines.push(words.slice(i, i + 2).join(" "));
        }
        return lines.length ? lines : [""];
    };

    const escapeHtml = (s = "") =>
        String(s)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    const formatTwoWordsPerLineHtml = (name = "") =>
        splitTwoWordsPerLine(name).map(escapeHtml).join("<br/>");


    const clearSearchState = React.useCallback((shouldNotifyParent = true) => {
        suppressAutoSearchRef.current = true;
        searchRequestIdRef.current += 1;
        searchRequestHistoryRef.current = [];
        //clearTimeout(timeoutRef.current);
        setIsLoading(false);
        onHasSubmittedSearchChange?.(false);
        setKeyword("");
        onKeywordChange?.("");
        setVendors([]);
        setProducts([]);
        setSelectedVendorId(null);
        setSelectedProductId(null);
        setActiveTab("products");
        setProductCount(0);
        setVendorCount(0);
        setCurrentPage(0);
        setTotalPages(1);
        lastSearchRef.current = null;
        setCtxMenu({ visible: false, x: 0, y: 0, type: null, item: null });
        setDiscoverKeywordTarget(KEYWORD_TARGETS.BOTH);
        onDiscoverKeywordTargetChange?.(KEYWORD_TARGETS.BOTH);
        onSearchRequestHistoryReplace?.([]);

        setDataModal(dm => ({ ...dm, visible: false }));
        setFunctionalityModal(dm => ({ ...dm, visible: false }));

        if (shouldNotifyParent) {
            onClearAll?.();
        }
    }, [onClearAll, onDiscoverKeywordTargetChange, onHasSubmittedSearchChange, onKeywordChange]);

    const handleClear = () => {
        clearSearchState(true);
    };

    const fetchPage = (newPage) => {
        if (!lastSearchRef.current) return;
        const { url, headers, payload } = lastSearchRef.current;
        setIsLoading(true);
        fetch(url, {
            method: "POST",
            headers: headers,
            body: JSON.stringify({ ...payload, page: newPage, size: PAGE_SIZE }),
        })
            .then(function (res) {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then(function (data) {
                var newVendors = data.vendors || [];
                var newProducts = data.products || [];
                setVendors(newVendors);
                setProducts(newProducts);
                setProductCount(typeof data.productCount === "number" ? data.productCount : newProducts.length);
                setVendorCount(typeof data.vendorCount === "number" ? data.vendorCount : newVendors.length);
                setCurrentPage(typeof data.page === "number" ? data.page : newPage);
                setTotalPages(typeof data.totalPages === "number" ? data.totalPages : 1);
                lastSearchRef.current.payload.page = newPage;
            })
            .catch(function (err) {
                console.error("Pagination fetch failed", err);
            })
            .finally(function () {
                setIsLoading(false);
            });
    };

    const fetchSuggestions = useCallback((value) => {
        clearTimeout(suggestTimerRef.current);

        const trimmed = (value || "").trim();
        if (trimmed.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        suggestTimerRef.current = setTimeout(() => {
            if (suggestAbortRef.current) suggestAbortRef.current.abort();
            const controller = new AbortController();
            suggestAbortRef.current = controller;

            const token = localStorage.getItem("token");
            fetch(
                `${process.env.REACT_APP_API_URL}/search/suggest?q=${encodeURIComponent(trimmed)}&mode=${mode}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    if (!controller.signal.aborted) {
                        setSuggestions(Array.isArray(data) ? data : []);
                        setShowSuggestions(Array.isArray(data) && data.length > 0);
                    }
                })
                .catch((err) => {
                    if (err.name !== "AbortError") console.error(err);
                });
        }, 250);
    }, [mode]);

    useEffect(() => {
        return () => {
            clearTimeout(suggestTimerRef.current);
            if (suggestAbortRef.current) suggestAbortRef.current.abort();
        };
    }, []);

    useEffect(() => {
        if (!clearResultsToken) return;
        clearSearchState(false);
    }, [clearResultsToken, clearSearchState]);

    const buildAllTabsWorkbook = (which, dataModal, functionalityModal) => {
        const modal = which === "data" ? dataModal : functionalityModal;

        const headerCol = which === "data" ? "Data Items" : "Functionality Items";
        const wb = XLSX.utils.book_new();

        [1, 2, 3].forEach((tab) => {
            const fctDto = modal.content?.[tab] || {};
            const { products = [], rows = [], productFeaturesMap = {} } = fctDto;

            if (!products.length || !rows.length) return;

            // AOA = array of arrays -> sheet
            const aoa = [];
            aoa.push([headerCol, ...products.map((p) => (p?.name ? String(p.name) : ""))]);

            rows.forEach((r) => {
                const label = r?.label ? String(r.label) : "";

                if (r?.mainHeader) {
                    aoa.push([label, ...products.map(() => "")]);
                    return;
                }

                aoa.push([
                    label,
                    ...products.map((p) => {
                        const feats = productFeaturesMap?.[p.id] || [];
                        const has = Array.isArray(feats) && feats.includes(r.featureId);
                        return has ? "✓" : "✗";
                    }),
                ]);
            });

            const ws = XLSX.utils.aoa_to_sheet(aoa);

            // Column widths (nice readability)
            ws["!cols"] = [
                { wch: 45 },
                ...products.map(() => ({ wch: 22 })),
            ];

            XLSX.utils.book_append_sheet(wb, ws, TAB_LABELS[tab] || `Tab ${tab}`);
        });

        return wb;
    };

    const handleExportAllTabsExcel = (which, dataModal, functionalityModal) => {
        try {
            const wb = buildAllTabsWorkbook(which, dataModal, functionalityModal);

            if (!wb.SheetNames?.length) {
                alert("Nothing to export.");
                return;
            }

            const baseFilename = which === "data" ? "fct-data" : "fct-functionality";
            const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });

            const blob = new Blob([out], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            downloadBlob(blob, `${baseFilename}.xlsx`);
        } catch (err) {
            console.error("Excel export failed", err);
            alert("Sorry, Excel export failed.");
        }
    };


    async function fetchCategoryItemNames(apiBase, ids) {
        if (!Array.isArray(ids) || !ids.length) return [];

        try {
            const res = await axios.get(`${apiBase}/category-items/names`, {
                params: { ids: ids.join(",") },
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            // assuming backend returns an array of { id, name }
            if (Array.isArray(res.data)) {
                return res.data.map((x) => x.name || "").filter(Boolean);
            }

            // fallback: object map { id: name }
            if (res.data && typeof res.data === "object") {
                return Object.values(res.data).filter(Boolean);
            }

            return [];
        } catch (err) {
            console.error("fetchCategoryItemNames failed", err);
            return [];
        }
    }

    async function fetchFeatureNames(apiBase, ids) {
        if (!Array.isArray(ids) || !ids.length) return [];

        try {
            const res = await axios.get(`${apiBase}/features/names`, {
                params: { ids: ids.join(",") },
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            if (Array.isArray(res.data)) {
                return res.data.map((x) => x.name || "").filter(Boolean);
            }

            if (res.data && typeof res.data === "object") {
                return Object.values(res.data).filter(Boolean);
            }

            return [];
        } catch (err) {
            console.error("fetchFeatureNames failed", err);
            return [];
        }
    }


    const RustyTick = () => (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            style={{ color: 'var(--rusty-orange)' }}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4 9.5l3.5 3.5L14 6"
                stroke="currentColor"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const RustyCross = () => (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            style={{color: '#25408f'}}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M5 5l8 8M13 5l-8 8"
                stroke="currentColor"
                strokeWidth="2.3"
                strokeLinecap="round"
            />
        </svg>
    );

    const TAB_LABELS = {
        1: "Fixed Income",
        2: "Equity",
        3: "Commodity",
    };

    const buildAllTabsHtml = (which, dataModal, functionalityModal) => {
        const modal = which === "data" ? dataModal : functionalityModal;
        const title =
            which === "data"
                ? "Data & Coverage – Data Items"
                : "Data & Coverage – Functionality Items";
        const headerCol = which === "data" ? "Data Items" : "Functionality Items";

        // we can just sum products across tabs (or you could max)
        const productCount = [1, 2, 3].reduce((acc, tab) => {
            const fctDto = modal.content?.[tab] || {};
            const products = fctDto.products || [];
            return acc + products.length;
        }, 0);

        let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body { font-family: 'DejaVu Sans', sans-serif; font-size: 12px; color: #000; }
    td, th { font-family: 'DejaVu Sans', sans-serif; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    h2 { font-size: 15px; margin-top: 16px; margin-bottom: 4px; }
    table { border-collapse: collapse; width: 100%; margin-top: 4px; }
    th, td { border: 1px solid #ccc; padding: 4px 6px; vertical-align: middle; }
    th { background: #25408f; color: #fff; text-align: center; }
    tr.main-header td { background: #e5e5e5; font-weight: 600; }
    .meta { font-size: 11px; margin-bottom: 10px; color: #555; }
  </style>
</head>
<body>
  <h1>${title}</h1>
`;

        [1, 2, 3].forEach((tab) => {
            const fctDto = modal.content?.[tab] || {};
            const { products = [], rows = [], productFeaturesMap = {} } = fctDto;
            if (!products.length || !rows.length) return;

            html += `<h2>${TAB_LABELS[tab] || `Tab ${tab}`}</h2>`;
            html += `<table><thead><tr>`;
            html += `<th style="text-align:left;">${headerCol}</th>`;
            products.forEach((p) => {
                const nameHtml = formatTwoWordsPerLineHtml((p && p.name) || "");
                html += `<th><div style="white-space:normal; line-height:1.15;">${nameHtml}</div></th>`;
            });
            html += `</tr></thead><tbody>`;

            rows.forEach((r) => {
                const isMainHeader = !!r.mainHeader;
                html += `<tr class="${isMainHeader ? "main-header" : ""}">`;
                html += `<td>${r.label || ""}</td>`;

                products.forEach((p) => {
                    const features = productFeaturesMap[p.id] || [];
                    const has = features.includes(r.featureId);
                    if (isMainHeader) {
                        html += `<td></td>`;
                    } else {
                        const symbol = has ? "&#10003;" : "&#10007;"; // ✓ / ✗
                        html += `<td style="text-align:center;font-size:14px;">${symbol}</td>`;
                    }
                });

                html += `</tr>`;
            });

            html += `</tbody></table>`;
        });

        html += `
</body>
</html>`;

        return html;
    };

    const downloadBlob = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const handlePrintAllTabs = (which, dataModal, functionalityModal) => {
        const html = buildAllTabsHtml(which, dataModal, functionalityModal);

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow || iframe.contentDocument;
        const frameDoc = frameWindow.document || iframe.contentDocument;

        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        setTimeout(() => {
            frameWindow.focus();
            frameWindow.print();

            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 100);
    };

    const handleExportAllTabsPdf = async (which, dataModal, functionalityModal) => {
        try {
            const html = buildAllTabsHtml(which, dataModal, functionalityModal);
            const token = localStorage.getItem("token");

            const scope = which === "data" ? "data" : "functionality";
            const baseFilename = scope === "data" ? "fct-data" : "fct-functionality";

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/exports/fct`,
                { html, scope, filename: baseFilename },
                {
                    params: { format: "pdf" },
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    responseType: "blob",
                }
            );

            downloadBlob(res.data, `${baseFilename}.pdf`);
        } catch (err) {
            console.error("PDF export failed", err);
            alert("Sorry, PDF export failed.");
        }
    };

    const handleExportAllTabsWord = async (which, dataModal, functionalityModal) => {
        try {
            const html = buildAllTabsHtml(which, dataModal, functionalityModal);
            const token = localStorage.getItem("token");

            const scope = which === "data" ? "data" : "functionality";
            const baseFilename = scope === "data" ? "fct-data" : "fct-functionality";

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/exports/fct`,
                { html, scope, filename: baseFilename },
                {
                    params: { format: "docx" },
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    responseType: "blob",
                }
            );

            downloadBlob(res.data, `${baseFilename}.docx`);
        } catch (err) {
            console.error("Word export failed", err);
            alert("Sorry, Word export failed.");
        }
    };

    const handleProductClick = id => {
          const product = products.find(p => p.id === id);
          openSheet?.("product", id, product?.name || `Product #${id}`);
          setSelectedProductId(id);
        };
    const handleVendorClick = (id, name) => {
        const vendor = vendors.find(v => v.id === id);
        openSheet?.("vendor", id, vendor?.name || name || `Vendor #${id}`);
        setSelectedVendorId(id);
    };

    const [dataModal, setDataModal] = useState({
        visible: false,
        productId: null,
        activeTab: 1,
        content: { 1: {}, 2: {}, 3: {} },
        loading: false
    });

    const [functionalityModal, setFunctionalityModal] = useState({
        visible: false,
        productId: null,
        activeTab: 1,
        content: { 1: {}, 2: {}, 3: {} },
        loading: false
    });


    const placeholder =
        searchMode === SEARCH_MODES.DISCOVER
            ? "Discover..."
            : searchMode === SEARCH_MODES.PRODUCT
                ? "Product search"
                : "Vendor search";

    const addKeywordButtonLabel =
        searchMode === SEARCH_MODES.DISCOVER
            ? "Search keyword"
            : searchMode === SEARCH_MODES.PRODUCT
                ? "Search product"
                : "Search vendor";

    const timeoutRef = useRef(null);
    useEffect(() => {
        const handleClick = () => {
            if (ctxMenu.visible) {
                setCtxMenu(c => ({ ...c, visible: false }));
            }
        };
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [ctxMenu.visible]);

    useEffect(() => {
        const handleClick = () => {
            if (searchModeMenuOpen) {
                setSearchModeMenuOpen(false);
            }
        };
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [searchModeMenuOpen]);

    useEffect(() => {
        const handleClearCompare = () => {
            setSelectedCompareIds([]);
        };

        window.addEventListener("profiler:clear-product-compare", handleClearCompare);
        return () =>
            window.removeEventListener(
                "profiler:clear-product-compare",
                handleClearCompare
            );
    }, []);

    useEffect(() => {
        const handler = (e) => {
            const { keyword,  mode: kind, match } = e.detail || {};
            if (!keyword) return;

            // Inject keyword
            setKeyword(keyword);

            // Set the correct mode
            if (mode === "vendor") {
                setSearchMode(SEARCH_MODES.VENDOR);
            } else if (mode === "product") {
                setSearchMode(SEARCH_MODES.PRODUCT);
            } else {
                setSearchMode(SEARCH_MODES.DISCOVER);
            }

        };

        window.addEventListener("profiler:keyword-search-internal", handler);
        return () =>
            window.removeEventListener("profiler:keyword-search-internal", handler);
    }, []);

    const discoverKeywordTerm = String(keywordValue || keyword || "").trim();
    const hasAnyDiscoverKeyword = discoverKeywordTerm.length > 0;

    const canSaveSearch =
        (
            (searchMode === SEARCH_MODES.DISCOVER ? hasAnyDiscoverKeyword : keyword.trim().length > 0) ||
            (productCategoryIds?.length ?? 0) > 0 ||
            (vendorCategoryIds?.length ?? 0) > 0 ||
            (featureIds?.length ?? 0) > 0
        ) &&
        (products.length > 0 || vendors.length > 0);

    const postSaveSearch = async (body: any) => {
        await axios.post(
            `${process.env.REACT_APP_API_URL}/save-searches`,
            body,
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        window.dispatchEvent(new CustomEvent("profiler:saved-search-created"));
    };

    const handleSaveSearch = async () => {
        const apiBase = process.env.REACT_APP_API_URL;

        const rawKw = keyword.trim();
        const discoverKeyword = searchMode === SEARCH_MODES.DISCOVER
            ? discoverKeywordTerm
            : rawKw;
        const hasKeyword = rawKw.length > 0;
        const hasDiscoverKeyword = discoverKeyword.length > 0;
        const hasProductCats = (productCategoryIds?.length ?? 0) > 0;
        const hasVendorCats = (vendorCategoryIds?.length ?? 0) > 0;
        const hasFeatures = (featureIds?.length ?? 0) > 0;
        const activeDiscoverFilterGroups = [
            hasDiscoverKeyword,
            hasProductCats,
            hasVendorCats,
            hasFeatures,
        ].filter(Boolean).length;

        // 1) Combination: any multi-filter discover search
        if (
            searchMode === SEARCH_MODES.DISCOVER &&
            activeDiscoverFilterGroups > 1
        ) {
            const productIds = productCategoryIds.map(Number).filter(Number.isFinite);
            const vendorIds = vendorCategoryIds.map(Number).filter(Number.isFinite);
            const selectedFeatureIds = featureIds.map(Number).filter(Number.isFinite);

            let suggested = "Combination search";
            try {
                const [productNames, vendorNames, featureNames] = await Promise.all([
                    productIds.length ? fetchCategoryItemNames(apiBase, productIds) : Promise.resolve([]),
                    vendorIds.length ? fetchCategoryItemNames(apiBase, vendorIds) : Promise.resolve([]),
                    selectedFeatureIds.length ? fetchFeatureNames(apiBase, selectedFeatureIds) : Promise.resolve([]),
                ]);
                const parts = [];
                if (hasDiscoverKeyword) parts.push(discoverKeyword);
                if (productNames.length) parts.push(`Products: ${productNames.join(", ")}`);
                if (vendorNames.length) parts.push(`Vendors: ${vendorNames.join(", ")}`);
                if (featureNames.length) parts.push(`Features: ${featureNames.join(", ")}`);
                if (parts.length) suggested = `Combination: ${parts.join(" + ")}`;
            } catch (e) {
            }

            const result = await openSaveDialog({ defaultName: suggested });
            if (!result || !result.name) return;

            await postSaveSearch({
                name: result.name,
                shared: !!result.shared,
                comparisonType: 4,
                ...(hasDiscoverKeyword ? { keyword: discoverKeyword } : {}),
                ...(productIds.length ? { categoryItemIds: productIds } : {}),
                ...(vendorIds.length ? { vendorCategoryItemIds: vendorIds } : {}),
                ...(selectedFeatureIds.length ? { featureIds: selectedFeatureIds } : {}),
            });
            return;
        }

        // 2) Pure keyword / name-based searches
        if ((searchMode === SEARCH_MODES.DISCOVER && hasDiscoverKeyword) || (searchMode !== SEARCH_MODES.DISCOVER && hasKeyword)) {
            let comparisonType;
            let suggestedName;
            let keywordToSave;

            if (searchMode === SEARCH_MODES.DISCOVER) {
                comparisonType = 8;              // Keyword All
                suggestedName = discoverKeyword;
                keywordToSave = discoverKeyword;
            } else if (searchMode === SEARCH_MODES.PRODUCT) {
                comparisonType = 6;              // Product Name
                suggestedName = `Product: ${rawKw}`;
                keywordToSave = rawKw;
            } else if (searchMode === SEARCH_MODES.VENDOR) {
                comparisonType = 5;              // Vendor Name
                suggestedName = `Vendor: ${rawKw}`;
                keywordToSave = rawKw;
            }

            const result = await openSaveDialog({
                defaultName: suggestedName || keywordToSave || rawKw,
            });
            if (!result || !result.name) return;

            await postSaveSearch({
                name: result.name,
                shared: !!result.shared,
                comparisonType,
                keyword: keywordToSave,
            });
            return;
        }

        // 3) Product Category
        if (hasProductCats) {
            const ids = productCategoryIds.map(Number).filter(Number.isFinite);
            if (!ids.length) return;

            let suggested = "Product Category search";
            try {
                const names = await fetchCategoryItemNames(apiBase, ids);
                suggested = names.join(", ");
            } catch (e) {}

            const result = await openSaveDialog({ defaultName: suggested });
            if (!result || !result.name) return;

            await postSaveSearch({
                name: result.name,
                shared: !!result.shared,
                comparisonType: 3,
                categoryItemIds: ids,
            });
            return;
        }

        // 4) Vendor Category (unchanged)
        if (hasVendorCats) {
            const ids = vendorCategoryIds.map(Number).filter(Number.isFinite);
            if (!ids.length) return;

            let suggested = "Vendor Category search";
            try {
                const names = await fetchCategoryItemNames(apiBase, ids);
                suggested = names.join(", ");
            } catch (e) {}

            const result = await openSaveDialog({ defaultName: suggested });
            if (!result || !result.name) return;

            await postSaveSearch({
                name: result.name,
                shared: !!result.shared,
                comparisonType: 12,
                vendorCategoryItemIds: ids,
            });
            return;
        }

        // 5) Features / FCT (unchanged)
        if ((featureIds?.length ?? 0) > 0) {
            const ids = featureIds.map(Number).filter(Number.isFinite);
            if (!ids.length) return;

            let suggested = "Feature search";
            try {
                const names = await fetchFeatureNames(apiBase, ids);
                suggested = names.join(", ");
            } catch (e) {}

            const result = await openSaveDialog({ defaultName: suggested });
            if (!result || !result.name) return;

            await postSaveSearch({
                name: result.name,
                shared: !!result.shared,
                comparisonType: 2,
                featureIds: ids,
            });
            return;
        }
    };

    // runs search
    useEffect(() => {
        if (suppressAutoSearchRef.current) {
            suppressAutoSearchRef.current = false;
            return;
        }
        if(!hasSubmittedSearch) {
            return;
        }
        const requestId = ++searchRequestIdRef.current;
        var hasKeyword   = keyword.trim() !== "";
        var hasSelectedKeyword = (keywordValue || "").trim().length > 0;
        var hasFeatures  = featureIds.length > 0;
        var hasVendorCats = vendorCategoryIds.length > 0;
        var hasProdCats   = productCategoryIds.length > 0;
        var featureIdsKey = featureIds.join(",");
        var vendorCategoryIdsKey = vendorCategoryIds.join(",");
        var productCategoryIdsKey = productCategoryIds.join(",");
        var currentDiscoverKeywordTarget =
            String(discoverKeywordTarget || "").trim() || KEYWORD_TARGETS.BOTH;
        var hasAnySearchCriteria =
            hasKeyword ||
            hasSelectedKeyword ||
            hasFeatures ||
            hasVendorCats ||
            hasProdCats;

        if (searchMode === SEARCH_MODES.DISCOVER) {
            if (!hasAnySearchCriteria) {
                setIsLoading(false);
                setVendors([]);
                setProducts([]);
                setProductCount(0);
                setVendorCount(0);
                return;
            }
        } else {
            // PRODUCT / VENDOR exact modes: if no keywords/filters but have other filters, use discover endpoint
            if (!hasKeyword) {
                if (!hasFeatures && !hasVendorCats && !hasProdCats) {
                    setIsLoading(false);
                    setVendors([]);
                    setProducts([]);
                    setProductCount(0);
                    setVendorCount(0);
                    return;
                }
            }
        }

		clearTimeout(timeoutRef.current);
		
        setIsLoading(true);
        timeoutRef.current = setTimeout(function () {
            if (requestId !== searchRequestIdRef.current) return;
            setVendors([]);
            setProducts([]);
            setIsLoading(true);

            var token = localStorage.getItem("token");
            var headers = {
                Authorization: "Bearer " + token,
                "Content-Type": "application/json",
            };

            var base = process.env.REACT_APP_API_URL;

            var searchPayload = {};
            var searchPath = "/search/all-keywords";
            var activeTrigger = searchTriggerRef.current;
            if (!activeTrigger) {
                if (featureIdsKey !== previousSearchInputsRef.current.featureIdsKey) {
                    activeTrigger = "feature";
                } else if (vendorCategoryIdsKey !== previousSearchInputsRef.current.vendorCategoryIdsKey) {
                    activeTrigger = "vendor_category";
                } else if (productCategoryIdsKey !== previousSearchInputsRef.current.productCategoryIdsKey) {
                    activeTrigger = "product_category";
                } else if (
                    currentDiscoverKeywordTarget !== previousSearchInputsRef.current.discoverKeywordTarget &&
                    (hasKeyword || hasSelectedKeyword)
                ) {
                    activeTrigger = SEARCH_MODES.DISCOVER;
                }
            }

            if (!activeTrigger) {
                if (hasKeyword || hasSelectedKeyword) {
                    activeTrigger =
                        searchMode === SEARCH_MODES.PRODUCT
                            ? SEARCH_MODES.PRODUCT
                            : searchMode === SEARCH_MODES.VENDOR
                                ? SEARCH_MODES.VENDOR
                                : SEARCH_MODES.DISCOVER;
                } else if (hasVendorCats) {
                    activeTrigger = "vendor_category";
                } else if (hasProdCats) {
                    activeTrigger = "product_category";
                } else if (hasFeatures) {
                    activeTrigger = "feature";
                }
            }

            previousSearchInputsRef.current = {
                featureIdsKey,
                vendorCategoryIdsKey,
                productCategoryIdsKey,
                discoverKeywordTarget: currentDiscoverKeywordTarget,
            };
            searchTriggerRef.current = null;

            if (activeTrigger === SEARCH_MODES.DISCOVER) {
                var discoverKeywordEntries = [];
                var pushDiscoverEntry = function (value, target) {
                    var term = String(value || "").trim();
                    if (!term) return;
                    discoverKeywordEntries.push({
                        term: term,
                        target: target || KEYWORD_TARGETS.BOTH,
                    });
                };

                if (keywordValue && keywordValue.trim().length > 0) {
                    pushDiscoverEntry(keywordValue, discoverKeywordTarget);
                }
                if (hasKeyword) {
                    pushDiscoverEntry(keyword, discoverKeywordTarget);
                }

                var dedupeEntries = function (entries) {
                    var seenTerms = new Set();
                    return entries.filter(function (entry) {
                        var key = entry.term.toLowerCase();
                        if (seenTerms.has(key)) return false;
                        seenTerms.add(key);
                        return true;
                    });
                };

                discoverKeywordEntries = dedupeEntries(discoverKeywordEntries);
                if (discoverKeywordEntries.length > 0) {
                    searchPayload.keyword = discoverKeywordEntries[0].term;
                }

                searchPayload.keywordSearchType = currentDiscoverKeywordTarget;
            } else if (activeTrigger === SEARCH_MODES.PRODUCT || activeTrigger === SEARCH_MODES.VENDOR) {
                var exactKeyword = String(keyword || "").trim();
                if (exactKeyword) {
                    searchPayload.keyword = exactKeyword;
                }
                searchPayload.keywordSearchType =
                    activeTrigger === SEARCH_MODES.PRODUCT
                        ? KEYWORD_TARGETS.PRODUCTS_ONLY
                        : KEYWORD_TARGETS.VENDORS_ONLY;
            } else if (activeTrigger === "vendor_category") {
                if (hasVendorCats) searchPayload.vendorCategoryIds = vendorCategoryIds.slice();
            } else if (activeTrigger === "product_category") {
                if (hasProdCats) searchPayload.productCategoryIds = productCategoryIds.slice();
            } else if (activeTrigger === "feature") {
                if (hasFeatures) searchPayload.featureIds = featureIds.slice();
            }

            // NOTE: previously this branch (for the "feature" / "vendor_category" /
            // "product_category" triggers — the only triggers that ever reach here,
            // since DISCOVER/PRODUCT/VENDOR are excluded above) re-sent the *currently
            // displayed* vendors/products as extra vendorIds/productIds constraints.
            // featureIds/vendorCategoryIds/productCategoryIds already carry the full,
            // cumulative set of everything currently ticked in the Builder tree, so the
            // backend's own AND-per-id query already combines multiple checkbox
            // selections correctly. Layering the previous response's ids on top made
            // each additional checkbox intersect against whatever happened to be on
            // screen from the last (often narrower, single-checkbox) auto-search,
            // instead of computing a fresh result from the full current selection —
            // e.g. checking "Real-time Prices" then also "Global Equity Markets" would
            // silently AND the second search against the first search's smaller result
            // set. That collapsed multi-filter Builder searches to a fraction of the
            // correct count. The deliberate, distinct feature for chaining separate
            // searches together (carrying one step's results into the next) is the
            // replaySearchHistoryToken effect below, which builds its own vendorIds/
            // productIds explicitly — this carryover was an accidental duplicate of
            // that behavior applied to ordinary same-screen filter changes.
            if (mode) searchPayload.mode = mode;
            searchPayload.page = 0;
            searchPayload.size = PAGE_SIZE;

            var searchUrl = base + searchPath;
            var requestHistoryEntry = {
                historyType: activeTrigger,
                method: "POST",
                url: searchUrl,
                payload: { ...searchPayload },
                vendors: [],
                products: [],
            };

            fetch(searchUrl, {
                method: "POST",
                headers: headers,
                body: JSON.stringify(searchPayload),
            })
                .then(function (res) {
                    if (!res.ok) throw new Error("HTTP " + res.status);
                    return res.json();
                })
                .then(function (data) {
                    if (requestId !== searchRequestIdRef.current) return;
                    var newVendors = data.vendors || [];
                    var newProducts = data.products || [];

                    setVendors(newVendors);
                    setProducts(newProducts);

                    setProductCount(
                        typeof data.productCount === "number"
                            ? data.productCount
                            : newProducts.length
                    );
                    setVendorCount(
                        typeof data.vendorCount === "number"
                            ? data.vendorCount
                            : newVendors.length
                    );
                    setCurrentPage(typeof data.page === "number" ? data.page : 0);
                    setTotalPages(typeof data.totalPages === "number" ? data.totalPages : 1);
                    lastSearchRef.current = { url: searchUrl, headers: headers, payload: { ...searchPayload } };

                    if (searchMode === SEARCH_MODES.VENDOR && newVendors.length > 0) {
                        setActiveTab("vendors");
                    } else if (newProducts.length > 0) {
                        setActiveTab("products");
                    } else if (newVendors.length > 0) {
                        setActiveTab("vendors");
                    }

                    onSearchRequestHistoryUpsert?.({
                        ...requestHistoryEntry,
                        vendors: newVendors,
                        products: newProducts,
                    });

                
                    
                })
                .catch(function (err) {
                    if (requestId !== searchRequestIdRef.current) return;
                    console.error(err);
                })
                .finally(function () {
                    if (requestId !== searchRequestIdRef.current) return;
                    onHasSubmittedSearchChange?.(false);
                    setIsLoading(false);
                });
        }, 500);
        
        return function () {
            clearTimeout(timeoutRef.current);
        };
    }, [
        //keyword,
        //keywordValue,
        hasSubmittedSearch,
        featureIds,
        vendorCategoryIds,
        productCategoryIds,
        discoverKeywordTarget,
        mode,
        onSearchRequestHistoryUpsert,
    ]);

    useEffect(() => {
        if (!replaySearchHistoryToken) return;
        if (!Array.isArray(searchRequestHistoryRef.current) || !searchRequestHistoryRef.current.length) return;

        clearTimeout(timeoutRef.current);

        const requestId = ++searchRequestIdRef.current;
        const token = localStorage.getItem("token");
        const headers = {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
        };

        setIsLoading(true);
        setVendors([]);
        setProducts([]);
        setProductCount(0);
        setVendorCount(0);

        let cancelled = false;

        const runReplay = async () => {
            let currentResults = { vendors: [], products: [] };
            const nextHistory = [];

            try {
                for (const [requestIndex, request] of searchRequestHistoryRef.current.entries()) {
                    if (cancelled || requestId !== searchRequestIdRef.current) return;

                    const nextPayload =
                        request?.payload && typeof request.payload === "object" && !Array.isArray(request.payload)
                            ? { ...request.payload }
                            : {};

                    if (requestIndex === 0) {
                        nextPayload.vendorIds = [];
                    } else if (currentResults.vendors.length > 0) {
                        nextPayload.vendorIds = currentResults.vendors.map((vendor) => vendor.id);
                    } else {
                        nextPayload.vendorIds = [];
                    }

                    if (requestIndex === 0) {
                        nextPayload.productIds = [];
                    } else if (currentResults.products.length > 0) {
                        nextPayload.productIds = currentResults.products.map((product) => product.id);
                    } else {
                        nextPayload.productIds = [];
                    }

                    const nextRequest = {
                        ...request,
                        payload: nextPayload,
                    };

                    const response = await fetch(String(request?.url || "").trim(), {
                        method: String(request?.method || "").trim().toUpperCase() || "POST",
                        headers,
                        body: JSON.stringify(nextPayload),
                    });

                    if (!response.ok) {
                        throw new Error("HTTP " + response.status);
                    }

                    const data = await response.json();
                    if (cancelled || requestId !== searchRequestIdRef.current) return;

                    currentResults = updateSearchResultsState(data);
                    nextHistory.push({
                        ...nextRequest,
                        vendors: currentResults.vendors,
                        products: currentResults.products,
                    });
                }

                onSearchRequestHistoryReplace?.(nextHistory);
            } catch (err) {
                if (!cancelled && requestId === searchRequestIdRef.current) {
                    console.error(err);
                }
            } finally {
                if (!cancelled && requestId === searchRequestIdRef.current) {
                    onHasSubmittedSearchChange?.(false);
                    setIsLoading(false);
                }
            }
        };

        runReplay();

        return function () {
            cancelled = true;
        };
    }, [
        replaySearchHistoryToken,
        onHasSubmittedSearchChange,
        onSearchRequestHistoryReplace,
    ]);

    const onContextMenu = (e, item, type) => {
        e.preventDefault();
        setCtxMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY,
            type,
            item
        });
    };


    const openDataModalForProducts = (productIds) => {
        if (!productIds || productIds.length === 0) return;

        const token = localStorage.getItem("token");
        const idsParam = Array.isArray(productIds) ? productIds.join(",") : productIds;

        setDataModal(dm => ({ ...dm, visible: true, productId: idsParam, loading: true }));
        axios.get(
            `${process.env.REACT_APP_API_URL}/data-fct`,
            {
                params: { multiProductIds: idsParam, mode },
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
            }
        )
            .then(({ data }) => setDataModal(dm => ({ ...dm, content: data, loading: false })))
            .catch(err => {
                console.error(err);
                setDataModal(dm => ({ ...dm, loading: false }));
            });
    };

    const openFunctionalityModalForProduct = (productId) => {
        const token = localStorage.getItem("token");

        setFunctionalityModal(dm => ({ ...dm, visible: true, productId, loading: true }));
        axios.get(
            `${process.env.REACT_APP_API_URL}/functionality-fct`,
            {
                params: { multiProductIds: productId, mode },
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
            }
        )
            .then(({ data }) => setFunctionalityModal(dm => ({ ...dm, content: data, loading: false })))
            .catch(err => {
                console.error(err);
                setFunctionalityModal(dm => ({ ...dm, loading: false }));
            });
    };
    const onViewData = () => {
        const productId = ctxMenu.item.id;
        setCtxMenu(c => ({ ...c, visible: false }));
        openDataModalForProducts(productId);
        };

    const onViewFunctionality = () => {
        const productId = ctxMenu.item.id;
        setCtxMenu(c => ({ ...c, visible: false }));
        openFunctionalityModalForProduct(productId);
    };

    useEffect(() => {
        if (isLoading) return;

        const hasKeyword   = keyword.trim() !== "";
        const hasProdCats  = Array.isArray(productCategoryIds) && productCategoryIds.length > 0;
        const hasVendCats  = Array.isArray(vendorCategoryIds) && vendorCategoryIds.length > 0;
        const hasFeatures  = Array.isArray(featureIds) && featureIds.length > 0;
        const hasResults   = (vendors.length > 0 || products.length > 0);

        const discoverHighlightKeyword =
            searchMode === SEARCH_MODES.DISCOVER
                ? String(keywordValue || keyword || "").trim()
                : "";

        const hasKeywordContext =
            searchMode === SEARCH_MODES.DISCOVER &&
            discoverHighlightKeyword.length > 0;

        if (hasResults && (hasKeywordContext || searchMode !== SEARCH_MODES.DISCOVER)) {
            window.dispatchEvent(
                new CustomEvent("profiler:results-context", {
                    detail: {
                        appMode: mode,
                        type: "keyword",
                        keyword: hasKeywordContext ? discoverHighlightKeyword : "",
                        keywords: [],
                        match: searchMode === SEARCH_MODES.DISCOVER ? "all" : "exact",
                        productCategoryItemIds: productCategoryIds.slice(),
                        vendorCategoryItemIds: vendorCategoryIds.slice(),
                        featureIds: featureIds.slice(),
                        featureIdsByModule,
                        keywordTarget:
                            searchMode === SEARCH_MODES.DISCOVER
                                ? discoverKeywordTarget
                                : searchMode === SEARCH_MODES.PRODUCT
                                    ? KEYWORD_TARGETS.PRODUCTS_ONLY
                                    : KEYWORD_TARGETS.VENDORS_ONLY,
                        keywordTargets: {},
                    },
                })
            );
        } else if (hasResults && hasProdCats) {
            window.dispatchEvent(new CustomEvent("profiler:results-context", {
                detail: { type: "product-category", categoryItemIds: productCategoryIds.slice() },
            }));
        } else if (hasResults && hasVendCats) {
            window.dispatchEvent(new CustomEvent("profiler:results-context", {
                detail: { type: "vendor-category", categoryItemIds: vendorCategoryIds.slice() },
            }));
        } else if (hasResults && hasFeatures) {
            window.dispatchEvent(new CustomEvent("profiler:results-context", {
                detail: { type: "features", featureIds: featureIds.slice() },
            }));
        } else {
            window.dispatchEvent(new CustomEvent("profiler:results-context", { detail: null }));
        }
    }, [
        isLoading,
        keyword,
        keywordValue,
        searchMode,
        productCategoryIds,
        vendorCategoryIds,
        featureIds,
        vendors,
        products,
        discoverKeywordTarget,
    ]);


    const hasCompareSelection = selectedCompareIds.length > 0;

    const handleCompareDataClick = () => {
        if (!hasCompareSelection) return;
        openDataModalForProducts(selectedCompareIds);
    };

    return (
<>
        <div className="searchbar">
            <div className="w-full mb-3 flex items-stretch gap-2">
                <div className="relative flex-1 flex items-stretch">
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={keyword}
                        onChange={e => {
                            const nextValue = e.target.value;
                            onHasSubmittedSearchChange?.(false);
                            setKeyword(nextValue);
                            fetchSuggestions(nextValue);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setShowSuggestions(false);
                                setSuggestions([]);
                            }
                            handleKeywordInputKeyDown(e);
                        }}
                        onFocus={() => {
                            if (suggestions.length > 0) setShowSuggestions(true);
                        }}
                        onBlur={() => {
                            setTimeout(() => setShowSuggestions(false), 200);
                        }}
                        className="searchbar-input flex-1 h-10 !mb-0 !leading-none !py-2 pr-16"
                        aria-label="Keyword search"
                        autoComplete="off"
                    />

                    {keyword.trim().length > 0 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onHasSubmittedSearchChange?.(false);
                                setKeyword("");
                                setSuggestions([]);
                                setShowSuggestions(false);
                            }}
                            className="absolute right-9 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-md border border-[var(--rusty-orange-light)] bg-white text-sm font-semibold leading-none text-[var(--rusty-orange)] hover:bg-[var(--rusty-orange-light)]"
                            title="Clear input"
                            aria-label="Clear input"
                        >
                            ×
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            setSearchModeMenuOpen(prev => !prev);
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                        title="Change search type"
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                        >
                            <path
                                d="M5 8l5 5 5-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>

                    {showSuggestions && suggestions.length > 0 && (
                        <div
                            className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto"
                        >
                            {suggestions.map((s, idx) => (
                                <button
                                    key={`${s.type}-${s.id}-${idx}`}
                                    type="button"
                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[var(--search-accent)]/10 transition-colors"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        setShowSuggestions(false);
                                        setSuggestions([]);
                                        setKeyword(s.name);
                                        if (s.type === "vendor") {
                                            openSheet?.("vendor", s.id, s.name);
                                        } else {
                                            openSheet?.("product", s.id, s.name);
                                        }
                                    }}
                                >
                                    <span
                                        className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded"
                                        style={{
                                            background: s.type === "vendor" ? "var(--search-pill-bg)" : "#f0f4ff",
                                            color: s.type === "vendor" ? "var(--search-accent)" : "#25408f",
                                            border: `1px solid ${s.type === "vendor" ? "var(--search-pill-border)" : "rgba(37,64,143,0.2)"}`,
                                        }}
                                    >
                                        {s.type === "vendor" ? "V" : "P"}
                                    </span>
                                    <span className="truncate">{s.name}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {searchModeMenuOpen && (
                        <div
                            className="absolute right-0 top-full mt-2 min-w-[12rem] rounded-xl border border-[rgba(37,64,143,0.18)] bg-white/95 shadow-[0_12px_30px_rgba(15,23,42,0.18)] z-30 text-xs backdrop-blur-sm"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
                                Search type
                            </div>

                            <div className="py-1">
                                {[SEARCH_MODES.DISCOVER, SEARCH_MODES.PRODUCT, SEARCH_MODES.VENDOR].map(mode => {
                                    const label =
                                        mode === SEARCH_MODES.DISCOVER
                                            ? "Discover"
                                            : mode === SEARCH_MODES.PRODUCT
                                                ? "Product search"
                                                : "Vendor search";

                                    const isActive = mode === searchMode;

                                    return (
                                        <button
                                            key={mode}
                                            type="button"
                                            className={
                                                "w-full px-3 py-2 flex items-center gap-2 text-left transition-colors " +
                                                (isActive
                                                    ? "bg-[var(--rusty-orange)]/8 text-[var(--threedi-blue,#25408f)]"
                                                    : "text-gray-600 hover:bg-gray-50")
                                            }
                                            onClick={() => {
                                                if (mode === searchMode) {
                                                    setSearchModeMenuOpen(false);
                                                    return;
                                                }

                                                suppressKeywordSyncRef.current = true;

                                                setSearchMode(mode);
                                                setSearchModeMenuOpen(false);

                                                // Preserve non-keyword filters and only clear the visible input.
                                                setKeyword("");
                                                onKeywordChange?.("");
                                                prevKeywordValueRef.current = "";
                                            }}
                                        >
                                            <span
                                                className={
                                                    "inline-flex h-2.5 w-2.5 rounded-full border " +
                                                    (isActive
                                                        ? "border-[var(--rusty-orange)] bg-[var(--rusty-orange)]/80"
                                                        : "border-gray-300 bg-white")
                                                }
                                            />
                                            <span className="truncate">{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                </div>

                <button
                    type="button"
                    onClick={handleClear}
                    disabled={!hasAnythingSelected}
                    className={`h-10 px-4 rounded-lg border text-sm font-semibold flex items-center justify-center ${
                        hasAnythingSelected
                            ? "text-[#25408f] border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                            : "text-gray-300 border-gray-200 cursor-not-allowed"
                    }`}
                    title="Clear keyword and filters"
                >
                    Clear
                </button>

                <button
                    type="button"
                    onClick={handleSaveSearch}
                    disabled={!canSaveSearch}
                    className={`h-10 px-4 rounded-lg border text-sm font-semibold flex items-center justify-center ${
                        canSaveSearch
                            ? "text-[var(--rusty-orange)] border-[var(--rusty-orange)] hover:bg-[var(--rusty-orange)]/10"
                            : "text-gray-300 border-gray-200 cursor-not-allowed"
                    }`}
                    title="Save this search"
                    aria-label="Save this search"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="mr-1">
                        <path d="M5 3h11l3 3v15H5zM7 3v6h8V3" fill="none" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    Save search
                </button>
            </div>

            {isLoading && <div className="loading-spinner" />}

            {!isLoading && !products.length && !vendors.length && !hasSubmittedSearch && (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 select-none">
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mb-4 text-gray-300"
                    >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <p className="text-sm">Type a keyword or select a category to get started</p>
                </div>
            )}

            {(products.length > 0 || vendors.length > 0) && (
                <div className="w-full max-w-4xl mx-auto">
                    {/* Tabs */}
                    <div
                        className="flex items-center justify-between border-b text-sm font-medium text-black mb-4"
                        style={{ borderBottomColor: "var(--search-accent-border)" }}
                    >
                        {/* Tabs (left) */}
                        <div className="flex">
                            {products.length > 0 && (
                                <button
                                    onClick={() => setActiveTab("products")}
                                    className={`px-4 py-2 -mb-[1px] border-b-2 transition-all duration-200 ${
                                        activeTab === "products"
                                            ? "text-[var(--search-accent)] border-[var(--search-accent)]"
                                            : "text-gray-500 border-transparent hover:text-[var(--search-accent)]"
                                    }`}
                                >
                                    Products
                                </button>
                            )}
                            {vendors.length > 0 && (
                                <button
                                    onClick={() => setActiveTab("vendors")}
                                    className={`px-4 py-2 -mb-[1px] border-b-2 transition-all duration-200 ${
                                        activeTab === "vendors"
                                            ? "text-[var(--search-accent)] border-[var(--search-accent)]"
                                            : "text-gray-500 border-transparent hover:text-[var(--search-accent)]"
                                    }`}
                                >
                                    Vendors
                                </button>
                            )}
                        </div>

                        {/* Counts (right) */}
                        <div className="flex items-center gap-2 text-xs">
                            {activeTab === "products" && productCount > 0 && (
                                <span
                                    className="px-2 py-1 rounded-full border text-xs"
                                    style={{
                                        background: "var(--search-pill-bg)",
                                        color: "var(--search-accent)",
                                        borderColor: "var(--search-pill-border)",
                                    }}
                                >
        {productCount} Product{productCount !== 1 ? "s" : ""}
      </span>
                            )}
                            {activeTab === "vendors" && vendorCount > 0 && (
                                <span
                                    className="px-2 py-1 rounded-full border text-xs"
                                    style={{
                                        background: "var(--search-pill-bg)",
                                        color: "var(--search-accent)",
                                        borderColor: "var(--search-pill-border)",
                                    }}
                                >
                                    {vendorCount} Vendor{vendorCount !== 1 ? "s" : ""}
      </span>
                            )}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="max-h-[450px] overflow-y-auto p-4 bg-white rounded-xl border border-gray-200 shadow-md">
                        {activeTab === "products" &&
                            products.map(p => (
                                <div
                                    key={p.id}
                                    className="result-item flex items-center justify-between gap-2"
                                    onClick={() => handleProductClick(p.id)}
                                    onContextMenu={e => onContextMenu(e, p, "product")}
                                >
                                    <div className="flex-1 min-w-0">
                                        {/* Product name */}
                                        <span className="truncate">{p.name}</span>

                                        {/* Vendor in brackets, clickable */}
                                        {p.vendor && typeof p.vendor.name === "string" && p.vendor.name.trim() && (
                                            <span className="ml-2 text-xs text-gray-500">
                                                {" ("}
                                                <button
                                                    type="button"
                                                    className="underline hover:text-[var(--search-accent)]"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleVendorClick(p.vendor.id, p.vendor.name);
                                                    }}
                                                >
                                                    {p.vendor.name}
                                                </button>
                                                {")"}
                                            </span>
                                        )}
                                    </div>

                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 flex-shrink-0"
                                        checked={selectedCompareIds.includes(p.id)}
                                        onChange={e => {
                                            e.stopPropagation();
                                            const checked = e.target.checked;
                                            setSelectedCompareIds(prev =>
                                                checked
                                                    ? [...prev, p.id]
                                                    : prev.filter(id => id !== p.id)
                                            );
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            window.dispatchEvent(
                                                new CustomEvent("profiler:toggle-product-compare", {
                                                    detail: { id: p.id, name: p.name },
                                                })
                                            );
                                        }}
                                        title="Select product for comparison"
                                    />
                                </div>
                            ))}


                        {activeTab === "vendors" &&
                            vendors.map(v => (
                                <div
                                    key={v.id}
                                    className="result-item"
                                    onClick={() => handleVendorClick(v.id)}
        >
                                    {v.name}
                </div>
                            ))}
                        {ctxMenu.visible && (
                            <ul
                                className="context-menu"
                                style={{
                                    position: 'fixed',
                                    top: ctxMenu.y,
                                    left: ctxMenu.x,
                                    listStyle: 'none',
                                    padding: '4px 0',
                                    margin: 0,
                                    background: '#fff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    borderRadius: '4px',
                                    zIndex: 1000,
                                }}
                            >
                                <li
                                    onClick={onViewData}
                                    style={{ padding: '8px 16px', cursor: 'pointer' }}
                                >
                                    View Data
                                </li>
                                <li onClick={onViewFunctionality} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                                    View Functionality
                                </li>
                            </ul>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3 px-1 text-sm">
                            <button
                                disabled={currentPage <= 0 || isLoading}
                                onClick={() => fetchPage(currentPage - 1)}
                                className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${
                                    currentPage <= 0 || isLoading
                                        ? "text-gray-300 border-gray-200 cursor-not-allowed"
                                        : "text-[var(--search-accent)] border-[var(--search-accent)] hover:bg-[var(--search-accent)]/10"
                                }`}
                            >
                                Previous
                            </button>
                            <span className="text-gray-600">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                                disabled={currentPage >= totalPages - 1 || isLoading}
                                onClick={() => fetchPage(currentPage + 1)}
                                className={`px-3 py-1.5 rounded border text-sm font-medium transition-colors ${
                                    currentPage >= totalPages - 1 || isLoading
                                        ? "text-gray-300 border-gray-200 cursor-not-allowed"
                                        : "text-[var(--search-accent)] border-[var(--search-accent)] hover:bg-[var(--search-accent)]/10"
                                }`}
                            >
                                Next
                            </button>
                        </div>
                    )}

                    </div>
            )}

            {/* No Results */}
            {hasSubmittedSearch &&
                !isLoading &&
                !vendors.length &&
                !products.length && (
                    <div className="no-results text-center text-sm text-gray-500 mt-4">
                        No results found.
                    </div>
                        )}

            {/* FCT Data Popup */}
            {dataModal.visible && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                >
                    <DraggableModalBox
                        onClose={() => {
                            setShowDataExportMenu(false);
                            setDataModal(dm => ({ ...dm, visible: false }));
                        }}
                    >
                        {/* Header: close on top line, tabs + export just below */}
                        <div className="mb-4">
                            {/* Close row */}
                            <div className="flex justify-end mb-2">
                                <button
                                    className="text-2xl font-bold leading-none px-2 hover:text-red-500"
                                    type="button"
                                    onClick={() => {
                                        setShowDataExportMenu(false);
                                        setDataModal(dm => ({ ...dm, visible: false }));
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Tabs + product count + export menu */}
                            <div className="flex items-center justify-between gap-4">
                                {/* Tabs */}
                                <div className="flex border-b border-gray-200 flex-shrink-0">
                                    {[1, 2, 3].map(tab => {
                                        const label =
                                            tab === 1 ? "Fixed Income" : tab === 2 ? "Equity" : "Commodity";
                                        const isActive = dataModal.activeTab === tab;
                                        return (
                                            <button
                                                key={tab}
                                                onClick={() => setDataModal(dm => ({ ...dm, activeTab: tab }))}
                                                className={`px-4 py-2 rounded border -mb-px transition ${
                                                    isActive
                                                        ? "bg-[var(--search-accent)] text-white"
                                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Right side: product count + 3-dots */}
                                <div className="flex items-center gap-3 text-sm text-gray-600 ml-auto">
                <span>
                    {(() => {
                        const dto = dataModal.content[dataModal.activeTab] || {};
                        const count = (dto.products || []).length;
                        return count
                            ? `${count} product${count !== 1 ? "s" : ""}`
                            : "";
                    })()}
                </span>

                                    <div className="relative">
                                        <button
                                            type="button"
                                            className="p-1 rounded hover:bg-gray-100"
                                            title="More options"
                                            onClick={() => setShowDataExportMenu(prev => !prev)}
                                        >
                                            <FaEllipsisV />
                                        </button>

                                        {showDataExportMenu && (
                                            <div className="absolute right-0 mt-2 w-44 rounded-md border bg-white shadow-lg z-20 text-sm">
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowDataExportMenu(false);
                                                        handlePrintAllTabs("data", dataModal, functionalityModal);
                                                    }}
                                                >
                                                    <FaPrint />
                                                    <span>Print all tabs</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowDataExportMenu(false);
                                                        handleExportAllTabsPdf("data", dataModal, functionalityModal);
                                                    }}
                                                >
                                                    <FaFilePdf />
                                                    <span>Export all tabs as PDF</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                    onClick={() => {
                                                        setShowDataExportMenu(false);
                                                        handleExportAllTabsWord("data", dataModal, functionalityModal);
                                                    }}
                                                >
                                                    <FaFileWord />
                                                    <span>Export all tabs as Word</span>
                                                </button>
                                                <button
                                                type="button"
                                                className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                onClick={() => {
                                                  setShowDataExportMenu(false);
                                                  handleExportAllTabsExcel("data", dataModal, functionalityModal);
                                                }}
                                                 >
                                                <FaFileExcel />
                                                <span>Export all tabs as Excel</span>
                                                + </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Body (unchanged) */}
                        {dataModal.loading ? (
                            <div className="text-center py-10">Loading…</div>
                        ) : (() => {
                            const fctDto = dataModal.content[dataModal.activeTab] || {};
                            const {
                                products: dtoProducts = [],
                                rows: dtoRows = [],
                                productFeaturesMap = {}
                            } = fctDto;

                            return (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full table-auto border-collapse">
                                        <thead>
                                        <tr className="bg-[#25408f] text-white">
                                            <th className="p-2 text-left align-middle">Data Items</th>
                                            {dtoProducts.map((p) => (
                                                <th
                                                    key={p.id}
                                                    className="p-2 text-center align-middle"
                                                    title={p.name}
                                                >
                                                    <div className="whitespace-normal break-words leading-tight">
                                                        {splitTwoWordsPerLine(p.name).map((line, idx) => (
                                                            <div key={idx}>{line}</div>
                                                        ))}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {dtoRows.map((r) => (
                                            <tr
                                                key={r.
                                                    featureId}
                                                className={`${r.mainHeader ? "bg-gray-200 font-semibold" : ""}`}
                                            >
                                                <td className="p-2 align-middle">{r.label}</td>

                                                {dtoProducts.map((p) => {
                                                    const has = (productFeaturesMap[p.id] || []).includes(r.featureId);
                                                    return (
                                                        <td key={p.id} className="p-2 align-middle">
                                                            {!r.mainHeader && (
                                                                <span className="inline-flex w-full items-center justify-center">
                  {has ? <RustyTick /> : <RustyCross />}
                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}
                    </DraggableModalBox>

                    </div>
            )}

            {functionalityModal.visible && (
                                        <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                >
                                            <DraggableModalBox
                                                onClose={() => {
                                                    setShowFuncExportMenu(false);
                                                    setFunctionalityModal(dm => ({ ...dm, visible: false }));
                                                }}
                                            >
                                                <div className="mb-4">
                                                    {/* Close row */}
                                                    <div className="flex justify-end mb-2">
                                                        <button
                                                            className="text-2xl font-bold leading-none px-2 hover:text-red-500"
                                                            type="button"
                                                            onClick={() => {
                                                                setShowFuncExportMenu(false);
                                                                setFunctionalityModal(dm => ({ ...dm, visible: false }));
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>

                                                    {/* Tabs + product count + export menu */}
                                                    <div className="flex items-center justify-between gap-4">
                                                        {/* Tabs */}
                                                        <div className="flex border-b border-gray-200 flex-shrink-0">
                                                            {[1, 2, 3].map(tab => {
                                                                const label =
                                                                    tab === 1 ? "Fixed Income" : tab === 2 ? "Equity" : "Commodity";
                                                                const isActive = functionalityModal.activeTab === tab;
                                                                return (
                                                                    <button
                                                                        key={tab}
                                                                        onClick={() =>
                                                                            setFunctionalityModal(dm => ({ ...dm, activeTab: tab }))
                                                                        }
                                                                        className={`px-4 py-2 rounded border -mb-px transition ${
                                                                            isActive
                                                                                ? "bg-[var(--search-accent)] text-white"
                                                                                : "border-transparent text-gray-500 hover:text-gray-700"
                                                                        }`}
                                                                    >
                                                                        {label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Right side: product count + 3-dots */}
                                                        <div className="flex items-center gap-3 text-sm text-gray-600 ml-auto">
                <span>
                    {(() => {
                        const dto =
                            functionalityModal.content[functionalityModal.activeTab] || {};
                        const count = (dto.products || []).length;
                        return count
                            ? `${count} product${count !== 1 ? "s" : ""}`
                            : "";
                    })()}
                </span>

                                                            <div className="relative">
                                                                <button
                                                                    type="button"
                                                                    className="p-1 rounded hover:bg-gray-100"
                                                                    title="More options"
                                                                    onClick={() => setShowFuncExportMenu(prev => !prev)}
                                                                >
                                                                    <FaEllipsisV />
                                                                </button>

                                                                {showFuncExportMenu && (
                                                                    <div className="absolute right-0 mt-2 w-44 rounded-md border bg-white shadow-lg z-20 text-sm">
                                                                        <button
                                                                            type="button"
                                                                            className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                                            onClick={() => {
                                                                                setShowFuncExportMenu(false);
                                                                                handlePrintAllTabs(
                                                                                    "functionality",
                                                                                    dataModal,
                                                                                    functionalityModal
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FaPrint />
                                                                            <span>Print all tabs</span>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                                            onClick={() => {
                                                                                setShowFuncExportMenu(false);
                                                                                handleExportAllTabsPdf(
                                                                                    "functionality",
                                                                                    dataModal,
                                                                                    functionalityModal
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FaFilePdf />
                                                                            <span>Export all tabs as PDF</span>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                                            onClick={() => {
                                                                                setShowFuncExportMenu(false);
                                                                                handleExportAllTabsWord(
                                                                                    "functionality",
                                                                                    dataModal,
                                                                                    functionalityModal
                                                                                );
                                                                            }}
                                                                        >
                                                                            <FaFileWord />
                                                                            <span>Export all tabs as Word</span>
                                                                        </button>
                                                                        <button
                                                                        type="button"
                                                                        className="flex w-full items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                                                        onClick={() => {
                                                                          setShowDataExportMenu(false);
                                                                          handleExportAllTabsExcel("data", dataModal, functionalityModal);
                                                                        }}
                                                                        >
                                                                        <FaFileExcel />
                                                                        <span>Export all tabs as Excel</span>
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body (existing table) */}
                                                {functionalityModal.loading ? (
                                                    <div className="text-center py-10">Loading…</div>
                        ) : (() => {
                            const fctDto = functionalityModal.content[functionalityModal.activeTab] || {};
                            const {
                                products: dtoProducts = [],
                                rows: dtoRows = [],
                                productFeaturesMap = {}
                            } = fctDto;

                            return (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full table-auto border-collapse">
                                        <thead>
                                        <tr className="bg-[#25408f] text-white">
                                            <th className="p-2 text-left align-middle">Functionality Items</th>
                                            {dtoProducts.map((p) => (
                                                <th
                                                    key={p.id}
                                                    className="p-2 text-center align-middle"
                                                    title={p.name}
                                                >
                                                    <div className="whitespace-normal break-words leading-tight">
                                                        {splitTwoWordsPerLine(p.name).map((line, idx) => (
                                                            <div key={idx}>{line}</div>
                                                        ))}
                                                    </div>
                                                </th>

                                            ))}
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {dtoRows.map((r) => (
                                            <tr
                                                key={r.featureId}
                                                className={r.mainHeader ? "bg-gray-200 font-semibold" : ""}
                                            >
                                                <td className="p-2 text-left align-middle">{r.label}</td>

                                                {dtoProducts.map((p) => {
                                                    const has = (productFeaturesMap[p.id] || []).includes(r.featureId);
                                                    return (
                                                        <td key={p.id} className="p-2 align-middle">
                                                            {!r.mainHeader && (
                                                                <div className="flex items-center justify-center">
                                                                    {has ? <RustyTick /> : <RustyCross />}
                                </div>
                        )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>

                    </div>
                            );
                        })()}
                       </DraggableModalBox>

                </div>
            )}

        </div>
    {saveDialogPortal}
</>
    );
}
